import { FC } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { useField } from 'formik';

import MainCard from 'src/components/MainCard/MainCard';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';
import { getCurrencySymbol } from 'src/utils/utils';


import OrdersList from '../OrdersList';
import { OrderType } from '../ServiceProducts';
import { PricingServiceTypes } from '../ServiceType';

interface ProductOrdersProps {
    serviceType: PricingServiceTypes;
    contractTerm: number;
    isEditingDisabled?: boolean;
}

export const ProductOrders: FC<ProductOrdersProps> = ({
    serviceType,
    contractTerm,
    isEditingDisabled = false,
}) => {
    const { setTotalOrderPrice, totalOrderPrice } = useServiceOrderContext();
    const [{ value: productOrder }, , { setValue: setProductOrder }] = useField({
        name: 'productOrder',
    });
    const [{ value: serviceOrder }, , { setValue: setServiceOrder }] = useField({
        name: 'order',
    });

    const { user } = useAuthContext();

    const currency = getCurrencySymbol(user?.business?.currency || 'EUR');

    const handleRemoveProduct = (id: number, type: OrderType) => {
        const productToRemove = productOrder?.[`${type}Products`]?.find(
            (product) => {return product?.id === id;}
        );

        setProductOrder({
            ...productOrder,
            total:
                productOrder?.total -
                (productToRemove?.monthlyPrice * productOrder.term + productToRemove?.oneOffPrice) *
                    productToRemove?.quantity,
            totalPrimaryMonthly:
                productToRemove.type === 'PRIMARY'
                    ? productOrder?.totalPrimaryMonthly -
                      productToRemove?.monthlyPrice * productToRemove?.quantity
                    : productOrder?.totalPrimaryMonthly,
            totalSecondaryMonthly:
                productToRemove.type === 'SECONDARY'
                    ? productOrder?.totalSecondaryMonthly -
                      productToRemove?.monthlyPrice * productToRemove?.quantity
                    : productOrder?.totalSecondaryMonthly,
            totalSetup:
                productOrder?.totalSetup - productToRemove?.oneOffPrice * productToRemove?.quantity,
            [`${type}Products`]:
                productOrder?.[`${type}Products`]?.filter((product) => {return product?.id !== id;}) ?? [],
        });

        setServiceOrder({
            ...serviceOrder,
            [`${type}`]: serviceOrder?.[type]?.filter((product) => {return product?.id !== id;}) ?? [],
        });

        const filteredMonthly = totalOrderPrice?.totalMonthly?.filter(
            (product) => {return product?.id !== id;}
        );
        const filteredOneOff = totalOrderPrice?.totalOneOff?.filter(
            (product) => {return product?.id !== id;}
        );

        setTotalOrderPrice({
            totalMonthly: filteredMonthly,
            totalOneOff: filteredOneOff,
        });
    };

    if (!productOrder) return null;

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <MainCard>
                <Typography fontSize="24px" color="#575757" marginBottom="8px">
                    Orders
                </Typography>
                <Typography fontSize="14px" color="#575757" marginBottom="8px">
                    View Order Table
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Typography fontSize="16px" color="#F18F34" marginBottom="8px">
                    Primary Order
                </Typography>
                {productOrder?.primaryProducts?.length === 0 || !productOrder?.primaryProducts ? (
                    <Typography fontSize="14px" color="#575757" marginBottom="8px">
                        N/A
                    </Typography>
                ) : (
                    <OrdersList
                        orders={productOrder?.primaryProducts}
                        removeProduct={handleRemoveProduct}
                        orderType="primary"
                        serviceType={serviceType}
                        contractTerm={contractTerm}
                        currency={currency}
                        isEditingDisabled={isEditingDisabled}
                    />
                )}
                <Divider
                    sx={{
                        my: '24px',
                    }}
                />

                <Typography fontSize="16px" color="#F18F34" marginBottom="8px">
                    Secondary Order
                </Typography>
                {productOrder?.secondaryProducts?.length === 0 ||
                !productOrder?.secondaryProducts ? (
                        <Typography fontSize="14px" color="#575757" marginBottom="8px">
                            N/A
                        </Typography>
                    ) : (
                        <OrdersList
                            orders={productOrder?.secondaryProducts}
                            removeProduct={handleRemoveProduct}
                            orderType="secondary"
                            serviceType={serviceType}
                            contractTerm={contractTerm}
                            currency={currency}
                            isEditingDisabled={isEditingDisabled}
                        />
                    )}
            </MainCard>
        </Box>
    );
};
export default ProductOrders;
